export const MAX_VOICE_NOTE_MS = 60_000;
export const MIN_VOICE_NOTE_MS = 1_000;
export const STORED_BARS = 100;

/** MP4 first so iOS plays every note; WebM only where MP4 can't be recorded (AI-3596). */
export const RECORD_FORMATS = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm'];

/** A stopped recording not yet sent. `url` plays the local blob. */
export interface VoiceClip {
  blob: Blob;
  url: string;
  durationMs: number;
  waveform: number[];
}

/** Loudest sample per bar, scaled to the loudest bar. */
export function toWaveform(samples: ArrayLike<number>, bars: number): number[] {
  const size = samples.length / bars;
  const levels: number[] = [];
  for (let bar = 0; bar < bars; bar++) {
    let peak = 0;
    const end = Math.floor((bar + 1) * size);
    for (let i = Math.floor(bar * size); i < end; i++) {
      const level = Math.abs(samples[i]!);
      if (level > peak) peak = level;
    }
    levels.push(peak);
  }
  const loudest = Math.max(...levels) || 1;
  return levels.map((level) => Math.round((level / loudest) * 100) / 100);
}

/** Down to `bars` peaks, so a narrow bubble keeps the shape. */
export function fitWaveform(waveform: number[], bars: number): number[] {
  if (waveform.length === 0) return Array.from({ length: bars }, () => 0);
  return waveform.length <= bars ? waveform : toWaveform(waveform, bars);
}

export function formatVoiceTime(ms: number): string {
  const seconds = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/** The backend matches the extension against a bare MIME type, so codec parameters go. */
export function voiceFile(clip: VoiceClip, now = Date.now()): File {
  const mime = clip.blob.type.split(';')[0]!.trim() || 'audio/webm';
  const ext = mime === 'audio/mp4' ? 'm4a' : 'webm';
  return new File([clip.blob], `voice-note-${now}.${ext}`, { type: mime });
}

export type MicProblem = 'blocked' | 'frame' | 'system' | 'missing' | 'busy' | 'lost' | 'failed';

export const MIC_PROBLEM_COPY: Record<MicProblem, string> = {
  blocked: "Microphone is blocked for this site. Allow it in your browser's site settings.",
  frame: "Voice notes can't be recorded on this page yet.",
  system: 'Microphone access is off for this browser in your device settings.',
  missing: 'No microphone found.',
  busy: 'Microphone is in use by another app.',
  lost: 'Microphone disconnected.',
  failed: 'Microphone could not be started.',
};

type MicBlockedReason = 'permission_denied' | 'not_supported' | 'no_device' | 'in_use' | 'other';

export function micBlockedReason(errorName: string): MicBlockedReason {
  switch (errorName) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'permission_denied';
    case 'NotSupportedError':
    case 'TypeError':
      return 'not_supported';
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'no_device';
    case 'NotReadableError':
    case 'AbortError':
      return 'in_use';
    default:
      return 'other';
  }
}
